import { useRef, useState } from "react";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { STARTER_QUESTIONS } from "@/lib/starters";
import { useTranslation } from "@/i18n";

export default function StartersForm({ initial, onSubmit, submitLabel, busy }) {
  const { t } = useTranslation();
  const sectionRefs = useRef({});
  const [attempted, setAttempted] = useState(false);
  const [value, setValue] = useState(() => ({
    q1: [...(initial?.q1 || [])],
    q2: [...(initial?.q2 || [])],
    q3: [...(initial?.q3 || [])],
    q4: [...(initial?.q4 || [])],
    q4Other: initial?.q4Other || "",
    q5: [...(initial?.q5 || [])],
  }));

  const toggle = (q, opt) => {
    setValue((prev) => {
      const cur = prev[q.id] || [];
      const has = cur.includes(opt);
      if (has) return { ...prev, [q.id]: cur.filter((o) => o !== opt) };
      if (cur.length >= q.max) return prev; // enforce max
      return { ...prev, [q.id]: [...cur, opt] };
    });
  };

  const isOk = (q) => (value[q.id] || []).length >= q.min;
  const allValid = STARTER_QUESTIONS.every(isOk);

  // The button is never left in a silent dead state: it is always tappable
  // (except while saving). If requirements are unmet, we reveal which question
  // is incomplete and scroll to it, instead of a greyed-out non-responsive button.
  const handleSubmit = () => {
    if (busy) return;
    if (!allValid) {
      setAttempted(true);
      const firstBad = STARTER_QUESTIONS.find((q) => !isOk(q));
      const node = firstBad && sectionRefs.current[firstBad.id];
      if (node && node.scrollIntoView) node.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    onSubmit(value);
  };

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="starters-form">
      {STARTER_QUESTIONS.map((q, idx) => {
        const selected = value[q.id] || [];
        const ok = selected.length >= q.min;
        const showError = attempted && !ok;
        const showOther = q.hasOther && selected.includes("other");
        return (
          <section
            key={q.id}
            ref={(node) => { sectionRefs.current[q.id] = node; }}
            data-testid={`starters-${q.id}`}
            className={[
              "scroll-mt-24",
              showError ? "-mx-3 rounded-2xl px-3 py-3 ring-1 ring-rose-300 bg-rose-50/40" : "",
            ].join(" ")}
          >
            <div className="flex items-baseline justify-between gap-3 mb-1">
              <h3 className="font-display text-base sm:text-lg font-semibold text-slate-900">
                {idx + 1}. {t(`starters.${q.id}Title`)}
              </h3>
            </div>
            <p className="text-xs mb-3">
              <span className={showError ? "text-rose-600 font-medium" : "text-slate-500"}>
                {t("starters.selectHint", { min: q.min, max: q.max })}
              </span>
              {" · "}
              <span className={ok ? "text-emerald-600" : showError ? "text-rose-600 font-medium" : "text-slate-500"}>
                {t("starters.selected", { count: selected.length })}
              </span>
            </p>
            <div className="flex flex-wrap gap-2">
              {q.options.map((opt) => {
                const active = selected.includes(opt);
                const atMax = !active && selected.length >= q.max;
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => toggle(q, opt)}
                    disabled={atMax}
                    className={[
                      "inline-flex items-center gap-1.5 rounded-full border px-3.5 h-10 text-sm transition touch-manipulation",
                      active
                        ? "bg-slate-900 text-white border-slate-900"
                        : atMax
                        ? "bg-white text-slate-300 border-slate-200 cursor-not-allowed"
                        : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50 hover:border-slate-400 active:bg-slate-100",
                    ].join(" ")}
                    data-testid={`starters-opt-${q.id}-${opt}`}
                  >
                    {active ? <Check className="h-3.5 w-3.5" /> : null}
                    {t(`starters.options.${opt}`)}
                  </button>
                );
              })}
            </div>
            {showOther ? (
              <Input
                value={value.q4Other}
                onChange={(e) => setValue((p) => ({ ...p, q4Other: e.target.value }))}
                placeholder={t("starters.otherPlaceholder")}
                className="rounded-xl h-11 mt-3"
                data-testid="starters-q4-other"
              />
            ) : null}
          </section>
        );
      })}

      <Button
        className="rounded-full h-12 w-full bg-slate-900 hover:bg-slate-800 disabled:opacity-60"
        disabled={busy}
        aria-disabled={!allValid}
        onClick={handleSubmit}
        data-testid="starters-submit"
      >
        {submitLabel}
      </Button>
    </div>
  );
}
